import React, { useState } from 'react';
import axios from 'axios';
import { Nfc, ExternalLink, Loader2, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NFC management card — mounted on BatchDetail. Supply Chain Manager can bind,
 * rebind, or clear the NFC sticker tag for the batch. Read-only for everyone else.
 *
 * Displays: Tag ID · Status · Last NFC Scan · Scan Count + the consumer-facing
 * URL operators should write onto the physical sticker.
 */
const NfcManagementCard = ({ batch, canManage, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [draftTag, setDraftTag] = useState(batch?.nfc_tag_id || '');
  const [saving, setSaving] = useState(false);

  const tag = batch?.nfc_tag_id || null;
  const status = batch?.nfc_status || (tag ? 'Active' : 'Unassigned');
  const lastScanRaw = batch?.nfc_last_scan_at;
  const scanCount = batch?.nfc_scan_count || 0;
  const stickerUrl = tag ? `${window.location.origin}/nfc/${encodeURIComponent(tag)}` : null;

  const startEdit = () => {
    setDraftTag(tag || '');
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraftTag(tag || '');
    setEditing(false);
  };
  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('mvp_token');
      const { data } = await axios.put(
        `${BACKEND_URL}/api/batches/${encodeURIComponent(batch.id)}/nfc`,
        { nfc_tag_id: draftTag.trim() || null },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      toast.success(draftTag.trim() ? `NFC tag ${draftTag.trim()} bound to batch` : 'NFC tag cleared');
      setEditing(false);
      if (typeof onUpdated === 'function') await onUpdated(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to update NFC tag';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const copyStickerUrl = async () => {
    if (!stickerUrl) return;
    try {
      await navigator.clipboard.writeText(stickerUrl);
      toast.success('Sticker URL copied to clipboard');
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  return (
    <section
      data-testid="nfc-management-section"
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center flex-shrink-0">
            <Nfc className="w-5 h-5 text-cyan-700" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
              NFC Sticker
            </h2>
            <p className="text-xs sm:text-sm text-slate-500">Bind a physical PureSustain NFC tag to this batch</p>
          </div>
        </div>
        <StatusPill status={status} testId="nfc-status-pill" />
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
        <Kpi label="Tag ID" value={tag || '—'} mono testId="nfc-kpi-tag-id" />
        <Kpi label="Status" value={status} testId="nfc-kpi-status" />
        <Kpi label="Last NFC Scan" value={fmtRel(lastScanRaw)} testId="nfc-kpi-last-scan" />
        <Kpi label="Scan Count" value={String(scanCount)} testId="nfc-kpi-scan-count" />
      </div>

      {/* URL to write onto the physical sticker */}
      {stickerUrl && !editing && (
        <div data-testid="nfc-sticker-url-card" className="bg-slate-50 border border-slate-200 rounded-lg p-3 sm:p-4 mb-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Write this URL to the physical NFC sticker
              </div>
              <div className="font-mono text-xs sm:text-sm text-slate-800 break-all">{stickerUrl}</div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyStickerUrl}
                data-testid="nfc-copy-url-button"
                className="h-9"
              >
                Copy URL
              </Button>
              <a
                href={stickerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nfc-open-scan-link"
                className="inline-flex items-center gap-1 px-3 h-9 text-sm font-medium rounded-md bg-slate-900 hover:bg-slate-800 text-white"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Test scan
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Manage controls */}
      {canManage && (
        editing ? (
          <form onSubmit={save} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 sm:p-4 flex flex-wrap items-end gap-3">
            <label className="flex-1 min-w-[180px]">
              <span className="block text-[11px] uppercase tracking-wider text-emerald-800 font-semibold mb-1">
                NFC Tag ID
              </span>
              <input
                type="text"
                value={draftTag}
                onChange={(e) => setDraftTag(e.target.value)}
                placeholder="e.g. NFC-000001"
                data-testid="nfc-edit-input"
                className="w-full h-10 px-3 rounded-md border border-emerald-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                autoFocus
              />
              <span className="block mt-1 text-[11px] text-emerald-700">
                Leave blank to clear the tag binding for this batch.
              </span>
            </label>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} data-testid="nfc-save-button" className="h-10 bg-emerald-700 hover:bg-emerald-800">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
              <Button type="button" variant="outline" onClick={cancelEdit} disabled={saving} data-testid="nfc-cancel-button" className="h-10">
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={startEdit}
            data-testid="nfc-edit-button"
            className="h-10"
          >
            {tag ? 'Re-bind / Clear NFC tag' : 'Bind NFC tag'}
          </Button>
        )
      )}

      {!canManage && !tag && (
        <p className="text-xs sm:text-sm text-slate-500">
          No NFC tag has been bound to this batch yet.
        </p>
      )}
    </section>
  );
};

/* -------- helpers -------- */

const Kpi = ({ label, value, mono, testId }) => (
  <div data-testid={testId} className="bg-slate-50 border border-slate-200 rounded-lg p-3 sm:p-4">
    <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
    <div className={`text-sm sm:text-base font-semibold text-slate-800 break-all ${mono ? 'font-mono' : ''}`}>
      {value || '—'}
    </div>
  </div>
);

const StatusPill = ({ status, testId }) => {
  const isActive = (status || '').toLowerCase() === 'active';
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider border ${
        isActive
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-slate-50 border-slate-200 text-slate-600'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
      {status || 'Unassigned'}
    </span>
  );
};

const fmtRel = (iso) => {
  if (!iso) return 'Never scanned';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'Never scanned';
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} h ago`;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

export default NfcManagementCard;
